import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    FaArrowLeft,
    FaCheckCircle,
    FaRegBuilding,
    FaRegCalendarAlt,
    FaPhone,
    FaEnvelope,
    FaRegCreditCard,
    FaFileAlt,
    FaExternalLinkAlt,
    FaChevronDown,
    FaChevronUp,
    FaDownload
} from "react-icons/fa";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { RotatingLines } from "react-loader-spinner";
import { getUserPurchasedMcaDocument } from "../../Services/APIrequests";
import Nav from "../../Components/LandingPage/Nav/Nav";
import Footer from "../../Components/LandingPage/Footer/Footer";
import styles from "./CompanymcadocDetails.module.css";

const documentLabels = {
    annualReturnsAndBalanceSheeteForms: "Annual Returns & Balance Sheets",
    certificates: "Certificates",
    changeDirectorsOrDesignatedPartner: "Change in Directors / Partners",
    chargeDocument: "Charge Documents",
    incorporationDocument: "Incorporation Documents",
    llpForms: "LLP Forms",
    othereFormDocuments: "Other e-Form Documents",
    other: "Other Documents"
};

const AccordionCard = ({ title, files, getFileName }) => {
    const [open, setOpen] = useState(files.length > 0);

    return (
        <div className={styles.accordionCard}>
            <button className={styles.accordionHeader} onClick={() => setOpen((p) => !p)}>
                <div className={styles.accordionLeft}>
                    <FaFileAlt className={styles.accordionIcon} />
                    <span className={styles.accordionTitle}>{title}</span>
                    {files.length > 0 && <span className={styles.accordionBadge}>{files.length}</span>}
                </div>
                {open ? <FaChevronUp className={styles.accordionChevron} /> : <FaChevronDown className={styles.accordionChevron} />}
            </button>

            {open && (
                <div className={styles.accordionBody}>
                    {files.length > 0 ? files.map((file, index) => {
                        const isObject = file && typeof file === "object";
                        const docUrl = isObject ? (file.document_url || file.fileurl) : file;
                        const docName = isObject ? (file.document_name || getFileName(docUrl)) : getFileName(file);
                        const attachments = isObject && Array.isArray(file.attachments) ? file.attachments : [];

                        return (
                            <div key={index} className={styles.docGroup}>
                                <div className={styles.fileRow}>
                                    <div className={styles.fileMainInfo}>
                                        <FaCheckCircle className={styles.successIcon} />
                                        <span className={styles.fileName}>{docName}</span>
                                    </div>
                                    <div className={styles.fileActions}>
                                        <button
                                            className={styles.viewLinkBtn}
                                            onClick={() => window.open(docUrl, "_blank")}
                                        >
                                            <FaExternalLinkAlt size={11} style={{ marginRight: 4 }} />
                                            View / Download
                                        </button>
                                    </div>
                                </div>

                                {attachments.length > 0 && (
                                    <div className={styles.attachmentsList}>
                                        {attachments.map((att, attIndex) => (
                                            <div key={attIndex} className={styles.attachmentRow}>
                                                <span className={styles.attachmentArrow}>↳</span>
                                                <button
                                                    className={styles.attachmentLinkBtn}
                                                    onClick={() => window.open(att.fileurl, "_blank")}
                                                >
                                                    {att.Name || getFileName(att.fileurl)}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    }) : (
                        <p className={styles.noUploadsText}>No documents uploaded</p>
                    )}
                </div>
            )}
        </div>
    );
};

const CompanymcadocDetails = () => {
    const { cin } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const docType = location.state?.docType || "old";

    const [docData, setDocData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isDownloadingAll, setIsDownloadingAll] = useState(false);

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const response = await getUserPurchasedMcaDocument(docType, cin);
                const data = response.data;

                if (data?.success) {
                    const doc =
                        data.purchasedMcaDocument ||
                        data.userPurchasedMcaDocument ||
                        data.mcaDocument ||
                        data.document ||
                        data.data ||
                        null;
                    setDocData(doc);
                } else {
                    setError("Document details not found.");
                }
            } catch (err) {
                console.error("Error fetching document details:", err);
                setError("Failed to load document details.");
            } finally {
                setIsLoading(false);
            }
        };

        if (cin) fetchDetails();
    }, [cin, docType]);

    const handleBack = () => navigate(-1);

    const getFileName = (url) => {
        if (!url) return "Document";
        try {
            return decodeURIComponent(url).split("/").pop();
        } catch {
            return url.split("/").pop();
        }
    };

    const handleDownloadAll = async (availableCats, companyNameStr) => {
        setIsDownloadingAll(true);
        try {
            const zip = new JSZip();
            const allFiles = [];

            availableCats.forEach(key => {
                if (Array.isArray(docData[key])) {
                    docData[key].forEach(file => {
                        if (file && typeof file === "object") {
                            const docUrl = file.document_url || file.fileurl;
                            if (docUrl) allFiles.push(docUrl);
                            if (Array.isArray(file.attachments)) {
                                file.attachments.forEach(att => {
                                    if (att.fileurl) allFiles.push(att.fileurl);
                                });
                            }
                        } else if (file) {
                            allFiles.push(file);
                        }
                    });
                }
            });

            if (allFiles.length === 0) {
                alert("No documents available to download.");
                setIsDownloadingAll(false);
                return;
            }

            const fetchPromises = allFiles.map(async (url) => {
                try {
                    const response = await fetch(url);
                    const blob = await response.blob();
                    const fileName = getFileName(url);
                    zip.file(fileName, blob);
                } catch (error) {
                    console.error("Error fetching file for zip:", url, error);
                }
            });

            await Promise.all(fetchPromises);

            const zipBlob = await zip.generateAsync({ type: "blob" });
            const zipFileName = `${companyNameStr.replace(/[^a-z0-9]/gi, '_')}_Documents.zip`;
            saveAs(zipBlob, zipFileName);
        } catch (error) {
            console.error("Error generating zip:", error);
            alert("Failed to download documents.");
        } finally {
            setIsDownloadingAll(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        try {
            return new Date(dateStr).toLocaleString("en-GB", {
                day: "2-digit", month: "short", year: "numeric",
                hour: "2-digit", minute: "2-digit", hour12: true
            });
        } catch {
            return dateStr;
        }
    };

    if (isLoading) {
        return (
            <>
                <Nav />
                <div className={styles.loadingContainer}>
                    <RotatingLines strokeColor="#3b82f6" strokeWidth="5" animationDuration="0.75" width="60" visible={true} />
                    <p>Fetching document details...</p>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !docData) {
        return (
            <>
                <Nav />
                <div className={styles.errorContainer}>
                    <p>{error || "Document details not found."}</p>
                    <button onClick={handleBack} className={styles.backBtn}>Go Back</button>
                </div>
                <Footer />
            </>
        );
    }

    const companyName = docData.CompanyName || docData.companyName || "Unknown Company";
    const cinValue = docData.CIN || docData.cin || cin;
    const purchaseDate = docData.dateOfPurchase || docData.createdAt || docData.purchaseDate;
    const status = docData.docOrderItemStatus || docData.status;
    const statusStr = status != null ? String(status) : "N/A";
    const amount = docData.price || docData.amount;

    const availableCategories = Object.keys(documentLabels);

    return (
        <>
            <Nav />
            <div className={styles.pageWrapper}>
                <div className={styles.container}>
                    <div className={styles.header}>
                        <button onClick={handleBack} className={styles.backIconButton}>
                            <FaArrowLeft />
                        </button>
                        <div>
                            <h1 className={styles.title}>{companyName}</h1>
                            <p className={styles.subtitle}>CIN: {cinValue}</p>
                        </div>
                    </div>

                    <div className={styles.mainLayout}>
                        {/* Sidebar */}
                        <div className={styles.sidebar}>
                            <div className={styles.infoCard}>
                                <div className={styles.infoList}>
                                    <div className={styles.infoItem}>
                                        <div className={styles.infoIconBg}>
                                            <FaRegCalendarAlt color="#3b82f6" />
                                        </div>
                                        <div className={styles.infoContent}>
                                            <p className={styles.infoLabel}>Purchase Date</p>
                                            <p className={styles.infoValue}>{formatDate(purchaseDate)}</p>
                                        </div>
                                    </div>

                                    <div className={styles.infoItem}>
                                        <div className={styles.infoIconBg}>
                                            <FaCheckCircle color="#10b981" />
                                        </div>
                                        <div className={styles.infoContent}>
                                            <p className={styles.infoLabel}>Status</p>
                                            <p className={styles.infoValue}>{statusStr}</p>
                                        </div>
                                    </div>

                                    {docData.userName && (
                                        <div className={styles.infoItem}>
                                            <div className={styles.infoIconBg}>
                                                <FaRegBuilding color="#8b5cf6" />
                                            </div>
                                            <div className={styles.infoContent}>
                                                <p className={styles.infoLabel}>Purchased By</p>
                                                <p className={styles.infoValue}>{docData.userName}</p>
                                            </div>
                                        </div>
                                    )}

                                    {docData.emailId && (
                                        <div className={styles.infoItem}>
                                            <div className={styles.infoIconBg}>
                                                <FaEnvelope color="#f59e0b" />
                                            </div>
                                            <div className={styles.infoContent}>
                                                <p className={styles.infoLabel}>Email ID</p>
                                                <p className={styles.infoValue}>{docData.emailId}</p>
                                            </div>
                                        </div>
                                    )}

                                    {docData.mobileNumber && (
                                        <div className={styles.infoItem}>
                                            <div className={styles.infoIconBg}>
                                                <FaPhone color="#ec4899" />
                                            </div>
                                            <div className={styles.infoContent}>
                                                <p className={styles.infoLabel}>Mobile Number</p>
                                                <p className={styles.infoValue}>{docData.mobileNumber}</p>
                                            </div>
                                        </div>
                                    )}

                                    {amount != null && (
                                        <div className={styles.infoItem}>
                                            <div className={styles.infoIconBg}>
                                                <FaRegCreditCard color="#6366f1" />
                                            </div>
                                            <div className={styles.infoContent}>
                                                <p className={styles.infoLabel}>Amount Paid</p>
                                                <p className={styles.infoValue}>₹{amount}</p>
                                            </div>
                                        </div>
                                    )}

                                    <div className={styles.infoItem}>
                                        <div className={styles.infoIconBg}>
                                            <FaFileAlt color="#0ea5e9" />
                                        </div>
                                        <div className={styles.infoContent}>
                                            <p className={styles.infoLabel}>Document Type</p>
                                            <p className={styles.infoValue} style={{ textTransform: "capitalize" }}>{docType}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Main Content Area */}
                        <div className={styles.contentArea}>
                            {availableCategories.length > 0 ? (
                                <div className={styles.documentsList}>
                                    {availableCategories.map((key) => (
                                        <AccordionCard
                                            key={key}
                                            title={documentLabels[key]}
                                            files={Array.isArray(docData[key]) ? docData[key] : []}
                                            getFileName={getFileName}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className={styles.accordionCard}>
                                    <div className={styles.emptyDocsState}>
                                        <FaFileAlt size={36} color="#cbd5e1" />
                                        <p className={styles.emptyDocsText}>No documents available yet</p>
                                        <p className={styles.emptyDocsSubtext}>Documents will appear here once they are processed.</p>
                                    </div>
                                </div>
                            )}

                            {availableCategories.length > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '20px' }}>
                                    <button 
                                        style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '8px', 
                                            padding: '12px 24px', 
                                            backgroundColor: '#3b82f6', 
                                            color: '#fff', 
                                            border: 'none', 
                                            borderRadius: '6px', 
                                            fontSize: '16px', 
                                            fontWeight: '500',
                                            cursor: isDownloadingAll ? 'not-allowed' : 'pointer',
                                            opacity: isDownloadingAll ? 0.7 : 1,
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                                        }}
                                        onClick={() => handleDownloadAll(availableCategories, companyName)}
                                        disabled={isDownloadingAll}
                                    >
                                        <FaDownload />
                                        {isDownloadingAll ? "Preparing ZIP..." : "Download All Documents"}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default CompanymcadocDetails;
